import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Home, Send, Clock, User, LogOut, Users, Shield, Star } from 'lucide-react';
import BottomNav from './BottomNav';
import { BRAND } from '../brand';

const baseNavItems = [
  { icon: Home, label: 'Inicio', path: '/dashboard' },
  { icon: Send, label: 'Enviar dinero', path: '/send-money' },
  { icon: Clock, label: 'Historial', path: '/transactions' },
  { icon: Users, label: 'Destinatarios', path: '/recipients' },
  { icon: User, label: 'Mi Perfil', path: '/profile' },
];

const ClientLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const navItems = [
    ...baseNavItems.slice(0, 4),
    ...((BRAND?.features?.gbPuntos && BRAND?.puntosProgramName) ? [{ icon: Star, label: BRAND.puntosProgramName, path: '/gb-puntos' }] : []),
    ...baseNavItems.slice(4),
  ];

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard' || location.pathname === '/';
    return location.pathname === path;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:fixed lg:inset-y-0 lg:left-0 lg:w-60 lg:flex-col bg-white border-r border-slate-200 z-50" data-testid="desktop-sidebar">
        {/* Logo */}
        <div className="py-8 flex items-center justify-center border-b border-slate-100 flex-shrink-0">
          <img src="/logo-oficial.png" alt={`${BRAND.name}`} className="h-32 cursor-pointer" onClick={() => navigate('/dashboard')} />
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems.map(({ icon: Icon, label, path }) => {
            const active = isActive(path);
            return (
              <button
                key={path}
                onClick={() => navigate(path)}
                data-testid={`sidebar-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  active
                    ? 'bg-green-50 text-green-700 font-semibold'
                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'
                }`}
              >
                <Icon size={19} strokeWidth={active ? 2.2 : 1.5} />
                {label}
              </button>
            );
          })}

          {user && !user.kyc_2026_completed && (
            <button
              onClick={() => navigate('/kyc-verify')}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 transition-all mt-3"
            >
              <Shield size={19} strokeWidth={1.5} />
              Verificar KYC
            </button>
          )}
        </nav>

        {/* User info */}
        <div className="p-3 border-t border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3 px-2 py-2">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold"
              style={{ backgroundColor: 'var(--brand-primary)' }}
              aria-hidden="true"
            >
              {(user?.full_name?.charAt(0) || 'U').toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">{user?.full_name?.split(' ')[0] || 'Usuario'}</p>
              <p className="text-[11px] text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="sidebar-logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition mt-1"
          >
            <LogOut size={16} />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <div className="lg:ml-60 min-h-screen">
        {children}
      </div>

      {/* Mobile bottom nav - hidden on desktop */}
      <div className="lg:hidden">
        <BottomNav />
      </div>
    </div>
  );
};

export default ClientLayout;
